import React, { useState, useEffect, useRef } from "react";
import "./Account.css";
import { RiImageAddLine } from "react-icons/ri";
import { Eye, EyeOff, ChevronLeft } from "lucide-react";
import {
  IdCardLanyard,
  School,
  Network,
  User,
  Mail,
  Briefcase,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import Avatar from "../Avatar";
import {
  useGetUserByEmailQuery,
  useUpdateUserImageMutation,
  useChangePasswordMutation,
  useGetAcademyByIdQuery,
  useGetDepartmentByIdQuery,
} from "../../slices/userApiSlice";
import { toast } from "sonner";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

const Account = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    employeeId: "",
    academyId: null,
    departmentId: null,
    academy: "",
    department: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    image: null,
    imageFile: null,
  });

  const [passwordStrength, setPasswordStrength] = useState({
    label: "",
    color: "",
  });

  const [isNewImageSelected, setIsNewImageSelected] = useState(false);
  const originalImageRef = useRef(null);
  const email = useSelector(getUserEmail);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const {
    data: userData,
    error,
    refetch,
  } = useGetUserByEmailQuery(email, {
    skip: !email, // Skip query if email is empty
  });

  // Get user role to determine which fields to show
  const userRole =
    useSelector((state) => state.auth.userInfo?.role?.name) ||
    userData?.user?.role?.name ||
    userData?.user?.role ||
    "";

  // Conditionally fetch academy and department data
  const { data: academyData } = useGetAcademyByIdQuery(profile.academyId, {
    skip: !profile.academyId || profile.academyId === "null",
  });

  const { data: departmentData } = useGetDepartmentByIdQuery(
    profile.departmentId,
    {
      skip: !profile.departmentId || profile.departmentId === "null",
    },
  );

  const [updateUserProfile, { isLoading: isImageLoading }] =
    useUpdateUserImageMutation();
  const [updateUserPassword, { isLoading: isPasswordLoading }] =
    useChangePasswordMutation();

  // Determine if this role should show academy/department fields
  // CFM: Academy
  // Manager: Academy, Department
  // Supervisor: Academy, Department, Division
  // Technician: Academy, Department, Division
  const shouldShowAcademy = [
    "Supervisor",
    "Technician",
    "Manager",
    "CFM",
    "cfm",
  ].includes(userRole);
  const shouldShowDepartment = ["Supervisor", "Technician", "Manager"].includes(
    userRole,
  );

  // Update user data
  useEffect(() => {
    if (userData?.user) {
      setProfile((prev) => ({
        ...prev,
        name: userData.user.name || "",
        email: userData.user.email || "",
        employeeId: userData.user.employeeId || "",
        academyId: userData.user.academyId || null,
        departmentId: userData.user.departmentId || null,
        image: userData.user.image || null,

        // Always reset passwords to empty so nothing gets pre-filled
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
      originalImageRef.current = userData.user.image || null;
    }

    if (error) {
      toast.error("Failed to load profile data.");
    }
  }, [userData, error]);

  // Update academy data
  useEffect(() => {
    if (academyData?.name) {
      setProfile((prev) => ({
        ...prev,
        academy: academyData.name || "",
      }));
    }
  }, [academyData]);

  // Update department data
  useEffect(() => {
    if (departmentData?.name) {
      setProfile((prev) => ({
        ...prev,
        department: departmentData.name || "",
      }));
    }
  }, [departmentData]);

  const togglePasswordVisibility = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value,
    }));

    if (name === "newPassword") {
      const strength = checkPasswordStrength(value);
      setPasswordStrength(strength);
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const checkPasswordStrength = (password) => {
    if (password.length === 0) return { label: "", color: "" };
    if (password.length < 6) return { label: "Weak", color: "red" };

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const criteriaMet = [
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
    ].filter(Boolean).length;

    if (criteriaMet >= 3 && password.length >= 8) {
      return { label: "Strong", color: "green" };
    } else if (criteriaMet >= 2 && password.length >= 6) {
      return { label: "Medium", color: "orange" };
    } else {
      return { label: "Weak", color: "red" };
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!profile.oldPassword.trim()) {
      newErrors.oldPassword = "Current password is required";
    }

    if (!profile.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (profile.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (profile.newPassword !== profile.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (profile.oldPassword === profile.newPassword) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setProfile((prev) => ({
          ...prev,
          image: event.target.result,
          imageFile: file,
        }));
        setIsNewImageSelected(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const result = await updateUserPassword({
        email: profile.email,
        oldPassword: profile.oldPassword,
        newPassword: profile.newPassword,
      }).unwrap();

      // Show success toast regardless of result structure
      setProfile((prev) => ({
        ...prev,
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      setPasswordStrength({ label: "", color: "" });
      setErrors({});

      // Show success toast
      toast.success("Password updated successfully!");
    } catch (error) {
      console.error("Password update error:", error);
      setErrors({ oldPassword: "Current password is incorrect" });
      toast.error(
        "Failed to update password. Please check your current password.",
      );
    }
  };

  const handleImageSubmit = async () => {
    try {
      if (!profile.imageFile) return;

      await updateUserProfile({
        email: userData.user.email,
        image: profile.imageFile,
      }).unwrap();

      setIsNewImageSelected(false);
      refetch();

      toast.success("Profile image updated successfully!");
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error(err?.data?.message || "Failed to update profile image.");
    }
  };

  const handleCancel = () => {
    if (isNewImageSelected) {
      setProfile((prev) => ({
        ...prev,
        image: originalImageRef.current,
        imageFile: null,
      }));
      setIsNewImageSelected(false);
    }

    setProfile((prev) => ({
      ...prev,
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    }));
    setPasswordStrength({ label: "", color: "" });
    setErrors({});
  };

  // Check if all password fields are filled
  const isPasswordFormValid =
    profile.oldPassword.trim() !== "" &&
    profile.newPassword.trim() !== "" &&
    profile.confirmPassword.trim() !== "";

  return (
    <div className="bg-white min-h-screen p-6">
      <div className="max-w-6xl">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <h2 className="text-2xl font-bold text-zinc-900">My Account</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Account Information */}
          <div className="space-y-6">
            <div className="flex flex-col max-w-lg w-full gap-4">
              {/* Profile Header */}
              <div className="bg-white border rounded-2xl shadow-sm overflow-hidden flex items-center gap-4 p-4 relative">
                <div className="relative flex-shrink-0">
                  <Avatar
                    src={profile.image}
                    alt={profile.name || "Profile"}
                    size={52}
                  />
                  <label
                    htmlFor="imageUpload"
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full cursor-pointer opacity-0 hover:opacity-100 transition-opacity"
                  >
                    <RiImageAddLine size={20} className="text-white" />
                  </label>
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-zinc-900">
                      {profile.name}
                    </h2>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                      {userRole}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm">{profile.email}</p>
                </div>
              </div>

              {isNewImageSelected && (
                <div className="flex justify-center gap-2">
                  <button
                    onClick={handleImageSubmit}
                    disabled={isImageLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                  >
                    {isImageLoading ? "Saving..." : "Save Image"}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Profile Details - Apple Style */}
              <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden p-4 py-2">
                {/* Name */}
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-cyan-600 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex items-center justify-between flex-1 py-3.5 border-b border-zinc-200">
                    <span className="text-sm text-zinc-900">Name</span>
                    <span className="text-sm text-zinc-400">
                      {profile.name || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-cyan-600 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex items-center justify-between flex-1 py-3.5 border-b border-zinc-200">
                    <span className="text-sm text-zinc-900">Email</span>
                    <span className="text-sm text-zinc-400">
                      {profile.email || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Role */}
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-cyan-600 flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex items-center justify-between flex-1 py-3.5 border-b border-zinc-200">
                    <span className="text-sm text-zinc-900">Role</span>
                    <span className="text-sm text-zinc-400">
                      {userRole || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Employee ID */}
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-zinc-400 flex items-center justify-center">
                    <IdCardLanyard className="w-4 h-4 text-white" />
                  </div>
                  <div
                    className={`flex items-center justify-between flex-1 py-3.5 ${
                      shouldShowAcademy || shouldShowDepartment
                        ? "border-b border-zinc-200"
                        : ""
                    }`}
                  >
                    <span className="text-sm text-zinc-900">Employee ID</span>
                    <span className="text-sm text-zinc-400">
                      {profile.employeeId || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Academy */}
                {shouldShowAcademy && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                      <School className="w-4 h-4 text-white" />
                    </div>
                    <div
                      className={`flex items-center justify-between flex-1 py-3.5 ${
                        shouldShowDepartment ? "border-b border-zinc-200" : ""
                      }`}
                    >
                      <span className="text-sm text-zinc-900">Academy</span>
                      <span className="text-sm text-zinc-400">
                        {profile.academy || "N/A"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Department */}
                {shouldShowDepartment && (
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                      <Network className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex items-center justify-between flex-1 py-3.5">
                      <span className="text-sm text-zinc-900">Department</span>
                      <span className="text-sm text-zinc-400">
                        {profile.department || "N/A"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Password Change */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800">
              Change Password
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.oldPassword ? "text" : "password"}
                    name="oldPassword"
                    value={profile.oldPassword}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 pr-10 border rounded ${
                      errors.oldPassword ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("oldPassword")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword.oldPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.oldPassword && (
                  <span className="text-red-500 text-sm">
                    {errors.oldPassword}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.newPassword ? "text" : "password"}
                    name="newPassword"
                    value={profile.newPassword}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 pr-10 border rounded ${
                      errors.newPassword ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("newPassword")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword.newPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {passwordStrength.label && (
                  <div className="text-sm mt-1">
                    Password strength:{" "}
                    <span
                      style={{
                        color: passwordStrength.color,
                        fontWeight: "bold",
                      }}
                    >
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
                {errors.newPassword && (
                  <span className="text-red-500 text-sm">
                    {errors.newPassword}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.confirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={profile.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 pr-10 border rounded ${
                      errors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("confirmPassword")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword.confirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="text-red-500 text-sm">
                    {errors.confirmPassword}
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-black text-white rounded hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handlePasswordSubmit}
                disabled={isPasswordLoading || !isPasswordFormValid}
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  color: "white",
                }}
              >
                {isPasswordLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
